import { CourseGPSVectorMapUpdateValues, courseMapElementLimit } from "json/constants";

const FRONT_HOLE_COUNT = 9;
const TOTAL_HOLE_COUNT = 18;

const GPS_COPY_KEY =
  /^(frontLat|frontLon|centerLat|centerLon|backLat|backLon|teeLat\d+|teeLon\d+|customLat\d+|customLon\d+|customDesc\d+|customName\d+|id_courseGPSName\d+)$/;

const isCoordPair = (lat, lon) => {
  const numLat = Number(lat);
  const numLon = Number(lon);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLon)) return false;
  return numLat !== 0 || numLon !== 0;
};

export const holeGpsHasData = (entry) => {
  if (!entry) return false;
  if (isCoordPair(entry.frontLat, entry.frontLon)) return true;
  if (isCoordPair(entry.centerLat, entry.centerLon)) return true;
  if (isCoordPair(entry.backLat, entry.backLon)) return true;
  for (let i = 1; i <= courseMapElementLimit.teeboxcenter; i++) {
    if (isCoordPair(entry[`teeLat${i}`], entry[`teeLon${i}`])) return true;
  }
  for (let i = 1; i <= courseMapElementLimit.hazard; i++) {
    if (isCoordPair(entry[`customLat${i}`], entry[`customLon${i}`])) return true;
  }
  return false;
};

const summarizeHoleRange = (holes) => {
  if (!holes.length) return "";
  if (holes.length === TOTAL_HOLE_COUNT) return "holes 1-18";
  if (holes.length === FRONT_HOLE_COUNT) {
    const isFrontNine = holes.every((hole) => hole >= 1 && hole <= FRONT_HOLE_COUNT);
    if (isFrontNine) return "holes 1-9";
    const isBackNine = holes.every(
      (hole) => hole >= FRONT_HOLE_COUNT + 1 && hole <= TOTAL_HOLE_COUNT
    );
    if (isBackNine) return "holes 10-18";
  }
  return `hole${holes.length === 1 ? "" : "s"} ${holes.join(", ")}`;
};

const getMissingHoleMessage = (holes, dataLabel) => {
  const holeSummary = summarizeHoleRange(holes);
  if (!holeSummary) return "";
  return `${holeSummary} ${holes.length === 1 ? "is" : "are"} empty for ${dataLabel}. Fill or duplicate them before flipping.`;
};

const getHolesMissingShapeData = (holeWiseData) => {
  const shapes = holeWiseData || [];
  const missingHoles = [];
  for (let hole = 1; hole <= TOTAL_HOLE_COUNT; hole++) {
    const holeHasShape = shapes.some(
      (shape) => shape?.hole === hole && shapeToPoints(shape).length > 0
    );
    if (!holeHasShape) {
      missingHoles.push(hole);
    }
  }
  return missingHoles;
};

const getHolesMissingGpsData = (gpsList) => {
  const missingHoles = [];
  for (let hole = 1; hole <= TOTAL_HOLE_COUNT; hole++) {
    if (!holeGpsHasData(gpsList?.[hole - 1])) {
      missingHoles.push(hole);
    }
  }
  return missingHoles;
};

export const evaluateFlipPreconditions = ({ layoutHoles, holeWiseData, gpsList }) => {
  if (Number(layoutHoles) !== TOTAL_HOLE_COUNT) {
    return {
      vectorMap: { allowed: false, reason: "Course must have 18 holes" },
      simpleMap: { allowed: false, reason: "Course must have 18 holes" },
      scorecard: { allowed: false, reason: "Course must have 18 holes" },
    };
  }

  const missingVectorHoles = getHolesMissingShapeData(holeWiseData);
  const missingGpsHoles = getHolesMissingGpsData(gpsList);

  return {
    vectorMap: {
      allowed: missingVectorHoles.length === 0,
      reason:
        missingVectorHoles.length === 0
          ? ""
          : getMissingHoleMessage(missingVectorHoles, "vector map"),
    },
    simpleMap: {
      allowed: missingGpsHoles.length === 0,
      reason:
        missingGpsHoles.length === 0
          ? ""
          : getMissingHoleMessage(missingGpsHoles, "simple map"),
    },
    scorecard: { allowed: true, reason: "" },
  };
};

export const evaluateDuplicatePreconditions = ({ layoutHoles, holeWiseData, gpsList }) => {
  if (Number(layoutHoles) !== TOTAL_HOLE_COUNT) {
    return { canDuplicate: false, reason: "Course must have 18 holes" };
  }
  const shapes = holeWiseData || [];
  for (let hole = 1; hole <= FRONT_HOLE_COUNT; hole++) {
    const holeHasShape = shapes.some(
      (shape) => shape?.hole === hole && shapeToPoints(shape).length > 0
    );
    if (!holeHasShape) {
      return {
        canDuplicate: false,
        reason: `Holes 1-9 must all have mapping data (hole ${hole} is empty)`,
      };
    }
  }
  for (let hole = FRONT_HOLE_COUNT + 1; hole <= TOTAL_HOLE_COUNT; hole++) {
    const holeHasShape = shapes.some(
      (shape) => shape?.hole === hole && shapeToPoints(shape).length > 0
    );
    const holeHasGps = holeGpsHasData(gpsList?.[hole - 1]);
    if (holeHasShape || holeHasGps) {
      const dataType = holeHasShape ? "vector data" : "GPS markers";
      return {
        canDuplicate: false,
        reason: `Holes 10-18 must be empty (hole ${hole} still has ${dataType})`,
      };
    }
  }
  return { canDuplicate: true, reason: "" };
};

const rawShapeHasGeometry = (shape) => {
  const points = Array.isArray(shape?.Points)
    ? shape.Points.join(" ")
    : shape?.Points;
  if (typeof points !== "string") return false;
  const values = points.trim().split(/[\s,]+/);
  for (let i = 0; i + 1 < values.length; i += 2) {
    if (isCoordPair(values[i], values[i + 1])) return true;
  }
  return false;
};

const rawHoleHasVectorData = (holeItem) => {
  if (!holeItem) return false;
  return Object.keys(holeItem).some((key) => {
    if (key === "HoleNumber") return false;
    const shapes = holeItem[key]?.Shapes?.Shape;
    return Array.isArray(shapes) && shapes.some(rawShapeHasGeometry);
  });
};

export const evaluateRawPreconditions = (rawCourseData, rawGPSList) => {
  if (Number(rawCourseData?.LayoutHoles) !== TOTAL_HOLE_COUNT) {
    return { canDuplicate: false, reason: "Course must have 18 holes" };
  }
  const holes = rawCourseData?.Holes?.Hole || [];
  const findHole = (holeNumber) =>
    holes.find((holeItem) => Number(holeItem?.HoleNumber) === holeNumber);
  for (let hole = 1; hole <= FRONT_HOLE_COUNT; hole++) {
    if (!rawHoleHasVectorData(findHole(hole))) {
      return {
        canDuplicate: false,
        reason: `Holes 1-9 must all have mapping data (hole ${hole} is empty)`,
      };
    }
  }
  for (let hole = FRONT_HOLE_COUNT + 1; hole <= TOTAL_HOLE_COUNT; hole++) {
    const holeHasShape = rawHoleHasVectorData(findHole(hole));
    const holeHasGps = holeGpsHasData(rawGPSList?.[hole - 1]);
    if (holeHasShape || holeHasGps) {
      const dataType = holeHasShape ? "vector data" : "GPS markers";
      return {
        canDuplicate: false,
        reason: `Holes 10-18 must be empty (hole ${hole} still has ${dataType})`,
      };
    }
  }
  return { canDuplicate: true, reason: "" };
};

const shapeToPoints = (shape) => {
  if (shape?.type === "point") {
    const flat = (shape?.list || []).flat(3);
    if (!isCoordPair(flat[0], flat[1])) return [];
    return [{ lat: flat[0], lng: flat[1] }];
  }
  const ring = shape?.list?.[0] || [];
  return ring
    .filter((pair) => Array.isArray(pair) && isCoordPair(pair[0], pair[1]))
    .map((pair) => ({ lat: pair[0], lng: pair[1] }));
};

const GPS_CHANNEL_LABELS = new Set(["Teeboxcenter", "Greencenter", "Fcb"]);

export const buildVectorCreateBodies = (holeWiseData, idCourse) => {
  const frontShapes = (holeWiseData || []).filter(
    (shape) =>
      shape?.hole >= 1 &&
      shape?.hole <= FRONT_HOLE_COUNT &&
      !GPS_CHANNEL_LABELS.has(shape?.label)
  );
  const bodies = [];
  frontShapes.forEach((shape) => {
    const type = CourseGPSVectorMapUpdateValues[shape?.type];
    const points = shapeToPoints(shape);
    if (type === undefined || points.length === 0) return;
    const body = {
      id_course: idCourse,
      holeNumber: shape.hole + FRONT_HOLE_COUNT,
      name: shape.label,
      type,
      points,
    };
    if (shape?.Attributes?.Type !== undefined && shape?.Attributes?.Type !== null) {
      body.typeAttribute = shape.Attributes.Type;
    }
    if (shape?.Attributes?.Size !== undefined && shape?.Attributes?.Size !== null) {
      body.sizeAttribute = shape.Attributes.Size;
    }
    if (shape?.Attributes?.Description !== undefined && shape?.Attributes?.Description !== null) {
      body.descriptionAttribute = shape.Attributes.Description;
    }
    bodies.push(body);
  });
  return bodies;
};

export const buildDuplicatedGPSList = (gpsList, gpsNameList) => {
  if (!Array.isArray(gpsList)) return null;
  const next = structuredClone(gpsList);
  while (next.length < TOTAL_HOLE_COUNT) {
    next.push({ holeNumber: next.length + 1 });
  }
  let copiedAny = false;
  for (let hole = 1; hole <= FRONT_HOLE_COUNT; hole++) {
    const source = next[hole - 1];
    if (!holeGpsHasData(source)) continue;
    copiedAny = true;
    const copiedFields = {};
    Object.keys(source).forEach((key) => {
      if (GPS_COPY_KEY.test(key)) {
        copiedFields[key] = source[key];
      }
    });
    for (let i = 1; i <= courseMapElementLimit.hazard; i++) {
      if (!isCoordPair(copiedFields[`customLat${i}`], copiedFields[`customLon${i}`])) continue;
      const nameEntry = (gpsNameList || []).find(
        (entry) => entry?.name === copiedFields[`customName${i}`]
      );
      if (nameEntry) {
        copiedFields[`id_courseGPSName${i}`] = nameEntry.id;
      }
    }
    const targetIndex = hole + FRONT_HOLE_COUNT - 1;
    next[targetIndex] = {
      ...next[targetIndex],
      ...copiedFields,
      holeNumber: hole + FRONT_HOLE_COUNT,
    };
  }
  return copiedAny ? next : null;
};
